import { HasInjector, InjectableInfo, RegistryEntry, RegistryKey } from './internal'
import { InjectableProvider } from './index'

/**
 * The Injector provides a method of dependency injection that removes the concept of what is injectable from the
 * concern of the Injector itself. This creates a one-way relationship.
 * The Injector provides the concept of a 'ChildInjector' via the Injector#createChildInjector method.
 * A child injector provides a mechanism for scoping dependencies or overriding/replacing dependencies at points
 * in the dependency graph.
 *
 * Usage:

 // This automatically provides the `InjectableDependency` in the root injector.
 @Injectable()
 class InjectableDependency { ... }

 // To fetch a dependency directly from the root injector:
 // Note: The `Injector` has a static `get` method to proxy to the root injector
 Injector.get(InjectableDependency)

 // To assign a dependency to a instance property:
 // Note: This will fetch the dependency from the 'nearest' injector.
 // i.e. if the instance receiving the dependency is itself an injectable dependency the Injector that provided
 // this instance will be used to fetch the dependency.
 class InjectionTarget {
     @Inject(InjectableDependency)
     private injected: InjectableDependency;
  }

 // To Lazily fetch a dependency when the property is first accessed:
 // Note: This can be used to work around circular references
 // Note: This replace the property on the class instance with a getter/setter pair.
 class InjectionTarget {
     @LazyInject(InjectableDependency)
     private injected: InjectableDependency;
  }

 // To create a child injector and provide dependencies to it:
 const childInjector = Injector.root.createChildInjector();
 childInjector.provide(InjectableDependency, { constructor: class extends InjectableDependency { ... } });

 *
 */

export class Injector {
	static readonly root: Injector = new Injector()

	private readonly registry: RegistryEntry[] = []

	private constructor(public readonly parentInjector?: Injector) { }

	/**
     * Provide an injectable for a given target symbol
     * @param target - Symbol used to retrieve injectable
     * @param provider - ProviderInfo used to create the injectable value
     * @param singleton - Should the injectable be provided as a singleton?
     */
	provide<T>(target: string | NoArgConstructor<T>, provider: InjectableProvider, singleton: boolean = true): void {
		// We've enforced the caller type, internally we'll need to work around the union type.
		const injectable = provider as any

		let factory: any
		if (injectable.constructor) {
			const constructor = addInjectorReference(injectable.constructor, this)
			factory = addCircularRefProtection(() => new constructor())
		} else if (injectable.factory) {
			factory = addCircularRefProtection(injectable.factory)
		}

		const injectableInfo = {
			singleton,
			factory,
			constructed: ('value' in provider),
			value: injectable.value,
		}

		this.addToRegistry(target, injectableInfo)
	}

	/**
     * Retrieve the injectable value matching the given target symbol.
     * @param target - Symbol used to retrieve injectable
     */
	get<T>(target: string | NoArgConstructor<T>): T {
		const injectable = this.findInRegistry(target)

		if (!injectable) {
			if (this.parentInjector) {
				return this.parentInjector.get(target)
			} else {
				throw new Error(`No provider found for "${target}"`)
			}
		}

		if (injectable.singleton && injectable.constructed) {
			return injectable.value as T
		}

		const dep = injectable.factory() as any
		injectable.constructed = true

		return injectable.value = dep
	}

	/**
     * Create a child injector that can be used to provide specialized injectables to only a subset of the system.
     */
	createChildInjector(): Injector {
		return new Injector(this)
	}

	/**
     * Proxy static 'get' through to the root injector 'get'
     * @see Injector#get
     */
	static get: typeof Injector.root.get = Injector.root.get.bind(Injector.root)

	private findInRegistry(registryKey: RegistryKey): InjectableInfo<HasInjector> | undefined {
		const registryEntry = this.registry.find(item => item.key === registryKey)

		return registryEntry && registryEntry.injectable
	}

	private addToRegistry(registryKey: RegistryKey, info: InjectableInfo<HasInjector>): void {
		const newInjectable = {
			key: registryKey,
			injectable: info,
		}

		const existingIndex = this.registry.findIndex(item => item.key === registryKey)

		if (existingIndex !== -1) {
			this.registry[existingIndex] = newInjectable
		} else {
			this.registry.push(newInjectable)
		}
	}
}

/**
 * Wrap the given factory in a function that will check if this factory is already in the current call stack.
 * This prevents an infinite loop and throws an error.
 * @param factory
 */
function addCircularRefProtection<T>(factory: () => T): () => T {
	let factoryInvoked = false
	return () => {
		if (factoryInvoked) {
			console.error(factory.toString())
			throw new Error('Circular reference detected while invoking injectable factory')
		}
		factoryInvoked = true
		const result = factory()
		factoryInvoked = false
		return result
	}
}

/**
 * Wrap the constructor so that the injector reference will be available during construction.
 * @param constructor - The constructor to be wrapped.
 * @param injector - The injector to provide.
 */
function addInjectorReference(constructor: NoArgConstructor<unknown>, injector: Injector): NoArgConstructor<unknown> {
	function extendWith(this: HasInjector) {
		this.__$injector = injector
		return constructor.call(this)
	}
	extendWith.prototype = Object.create(constructor.prototype)
	Object.defineProperty(extendWith.prototype, 'constructor', {
		value: extendWith,
		enumerable: false, // so that it does not appear in 'for in' loop
		writable: true,
	})

	return extendWith as any
}


